import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { TemplateViewPopover } from './TemplateViewPopover';
import type { TemplateView } from '../types';

interface TemplateViewsTabProps {
  views: TemplateView[];
  editable: boolean;
  onAdd?: (view: TemplateView) => void;
  onRemove?: (index: number) => void;
  isPending?: boolean;
}

export function TemplateViewsTab({
  views,
  editable,
  onAdd,
  onRemove,
  isPending = false,
}: TemplateViewsTabProps) {
  const { t } = useTranslation('projectTemplates');

  return (
    <div className="flex flex-col gap-2" data-testid="project-templates-views-tab">
      {views.length === 0 ? (
        <p
          className="py-4 text-center text-sm text-muted-foreground"
          data-testid="project-templates-views-empty"
        >
          {t('preview.emptyStates.views')}
        </p>
      ) : (
        views.map((view, index) => (
          <div
            key={`${view.type}-${index}`}
            className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
            data-testid={`project-templates-view-row-${index}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm">{view.name}</span>
              {view.isDefault && (
                <Badge variant="secondary" className="font-normal">
                  {t('preview.defaultView')}
                </Badge>
              )}
            </div>
            {editable && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    disabled={isPending}
                    aria-label={t('actions.remove')}
                    onClick={() => onRemove?.(index)}
                    data-testid={`project-templates-view-remove-${index}`}
                  >
                    <X className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('actions.remove')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        ))
      )}

      {editable && onAdd && (
        <TemplateViewPopover existingViews={views} onAdd={onAdd} isPending={isPending} />
      )}
    </div>
  );
}
